import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { DataGridPremium, GridActionsCellItem, GridToolbar, useGridApiRef } from "@mui/x-data-grid-premium";
import axios from "axios";
import dayjs from "dayjs";
import { enqueueSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { API } from "../../../../helpers/constants";
import useUserData from "../../../../hooks/user/useUserData";
import { CustomPagination } from "../../../../components/mui/grids/dataGridCustom/customPagination";
import DataGridWrapper from "../../../../components/mui/grids/dataGridWrapper/dataGridWrapper";
import { muiGridLocales } from "../../../../helpers/mui/muiLocales";
import { getRouteUrl } from "../../../../helpers/routing/getRouteUrl";
import { useMuiGridServerSide } from "../../../../hooks/muiGrid/useMuiGridServerSide";
import { CHECKLISTS } from "../../../../locales/pages/namespaces";
import { ROUTE_PAGE_QUESTIONNAIRE_PREVIEW, ROUTE_PAGE_QUESTIONNAIRE_PREVIEW_BY_HEADER } from "../../routes/routes";

const ChecklistsList = (props) => {
	const { t } = useTranslation(CHECKLISTS)
	const apiRef = useGridApiRef();
	const { language, dateFormat, timeFormat, token } = useUserData();
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [selectedChecklistId, setSelectedChecklistId] = useState(null);
	const [selectedChecklistDescription, setSelectedChecklistDescription] = useState('');
	const [isDeleting, setIsDeleting] = useState(false);
	
	const {
		isLoading,
		data,
		total,
		page,
		pageSize,
		onPaginationModelChange,
		onSortModelChange,
		onFilterModelChange,
		onGroupModelChange,
		refreshGrid,
	} = useMuiGridServerSide({
		url: props.url,
		initialPageSize: 50,
		initialSortModel: [{ name: 'id', direction: 'descending' }],
		otherDataParams: props.IssueId ? {IssueId: props.IssueId}  : props.Finalization ?  {Finalization: props.Finalization} : null,
	})

	useEffect(() => {
		props.onRefreshReady?.(refreshGrid)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const handleDeleteClick = (id, description) => {
		setSelectedChecklistId(id);
		setSelectedChecklistDescription(description || '');
		setDeleteDialogOpen(true);
	};

	const handleCloseDeleteDialog = () => {
		setDeleteDialogOpen(false);
		setSelectedChecklistId(null);
		setSelectedChecklistDescription('');
	};

	const handleConfirmDelete = async () => {
		setIsDeleting(true);
		try {
			const response = await axios({
				method: 'post',
				data: {
					id: selectedChecklistId,
				},
				url: `${API}/en/questionnaire-rule/dismissal`,
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`
				},
			});

			// Check for specific error message
			if (response.data.message === "You can't delete this survey, because it's connected to «Good to Have».") {
				const warningMessage = language === 'el'
					? 'Δεν μπορείτε να διαγράψετε αυτήν την έρευνα, επειδή είναι συνδεδεμένη με «Good to Have».'
					: "You can't delete this survey, because it's connected to «Good to Have».";
				
				enqueueSnackbar(warningMessage, {
					variant: 'warning',
					autoHideDuration: 5000,
				});
				handleCloseDeleteDialog();
				setIsDeleting(false);
				return;
			}

			// Check for general errors
			if (response.data.message === 'errors') {
				enqueueSnackbar(response.data.errors || t('Failed to delete checklist'), {
					variant: 'error',
					autoHideDuration: 3000,
				});
				setIsDeleting(false);
				return;
			}

			// Success
			enqueueSnackbar(t('Checklist deleted successfully'), {
				variant: 'success',
				autoHideDuration: 2000,
			});
			handleCloseDeleteDialog();
			setIsDeleting(false);
			refreshGrid();

		} catch (error) {
			setIsDeleting(false);
			
			// Check if error response contains the specific message
			const errorMessage = error?.response?.data?.message || error?.response?.data || '';
			if (errorMessage.includes("You can't delete this survey") || errorMessage.includes("Good to Have")) {
				const warningMessage = language === 'el'
					? 'Δεν μπορείτε να διαγράψετε αυτήν την έρευνα, επειδή είναι συνδεδεμένη με «Καλώς Έχειν».'
					: "You can't delete this survey, because it's connected to «Good to Have».";
				
				enqueueSnackbar(warningMessage, {
					variant: 'warning',
					autoHideDuration: 5000,
				});
			} else {
				enqueueSnackbar(error?.response?.data?.errors || error?.response?.data?.message || t('Failed to delete checklist'), {
					variant: 'error',
					autoHideDuration: 3000,
				});
			}
		}
	};

	const columns = useMemo(() => [
		{
			headerName: t('Actions'),
			field: 'actions',
			type: 'actions',
			width: 120,
			getActions: (params) => {
				if (params.id === 'auto-generated-group-footer-root') {
					// Return no actions for aggregation rows
					return []
				}
				// Return default actions for regular rows
				return [
					<GridActionsCellItem
						component={Link}
						to={params.row.details === 1 ? getRouteUrl(ROUTE_PAGE_QUESTIONNAIRE_PREVIEW_BY_HEADER, { headerId: params.id }) : getRouteUrl(ROUTE_PAGE_QUESTIONNAIRE_PREVIEW, { questionnaireId: params.row.QuestionnaireId, headerId:params.row.id })}
						icon={<EditIcon/>}
						label={t('Edit')}
						color={'info'}
					/>,
					<GridActionsCellItem
						icon={<DeleteIcon/>}
						label={t('Delete')}
						onClick={() => handleDeleteClick(params.id, params.row.Description)}
						color={'error'}
					/>
				]
			}
		},
		{
			field: "Description",
			headerName: t("Description"),
			minWidth: 50, flex: 1,
			aggregable: false,
		},
		{
			field: "Comment",
			headerName: t("Comment"),
			minWidth: 50, flex: 1,
			aggregable: false,
		},
		{
			headerName: t('Last Modified At'),
			field: 'LastModifiedAt',
			minWidth: 160,
			flex: 1,
			type: 'date',
			aggregable: false,
			valueFormatter: (value, row, column, apiRef) => {
				return (value ? dayjs(value).format(`${dateFormat} ${timeFormat}`) : null)
			}
		},
		{
			field: "LoginName",
			headerName: t("Last Modified By"),
			minWidth: 50, flex: 1,
			aggregable: false,
		},
		...(props.showReferenceDt ? [
			{
				field: 'ucReferenceDt',
				headerName: t('Month'),
				description: t('Month'),
				flex: 1,
				minWidth: 100,
				type: 'date',
				align: 'center',
				filterable: false,
				aggregable: false,
				valueFormatter: (value, row, column, apiRef) => {
					return (value ? dayjs(value).format('MMMM YYYY') : null)
				}
			},
		] : []),
	], [t, dateFormat, timeFormat])
	
	return (
		<DataGridWrapper sx={{ height: 700 }}>
			<DataGridPremium
				apiRef={apiRef}
				columns={columns}
				rows={data}
				pagination
				paginationModel={{ page, pageSize }}
				paginationMode="server"
				onPaginationModelChange={onPaginationModelChange}
				rowCount={Number(total)}
				pageSizeOptions={[5, 10, 15, 20, 50, 100]}
				sortingMode="server"
				onSortModelChange={(sortModel) => onSortModelChange(sortModel)}
				filterMode="server"
				onRowGroupingModelChange={onGroupModelChange}
				onFilterModelChange={onFilterModelChange}
				loading={isLoading}
				localeText={muiGridLocales(language)}
				initialState={{
					density: 'compact',
				}}
				slots={{
					toolbar: GridToolbar,
					pagination: CustomPagination,
				}}
			
			/>
			<Dialog
				open={deleteDialogOpen}
				onClose={!isDeleting ? handleCloseDeleteDialog : undefined}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>
					{t('Are you sure you want to delete this checklist?')}
				</DialogTitle>
				<DialogContent dividers>
					<Stack
						direction={'row'}
						spacing={1}
						p={1}
					>
						<Typography
							variant={'subtitle2'}
							color={'error'}
							component={'div'}
						>
							{t('Checklist')}:
						</Typography>
						<Typography
							variant={'body2'}
							component={'div'}
						>
							{selectedChecklistDescription}
						</Typography>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={handleCloseDeleteDialog}
						disabled={isDeleting}
						sx={{
							marginRight: 'auto'
						}}
					>
						{t('Cancel')}
					</Button>
					<Button
						variant="contained"
						color={'error'}
						onClick={handleConfirmDelete}
						disabled={isDeleting}
					>
						{isDeleting ? t('Deleting...') : t('Confirm')}
					</Button>
				</DialogActions>
			</Dialog>
		</DataGridWrapper>
	);
};

export default ChecklistsList